<template>
  <div aria-live="polite" v-html="liveFilterText" class="sr-only" />
  <SearchForm v-if="showSearchForm" @submit.prevent="onSubmit" />
  <section class="container">
    <div v-if="allowFetchFilters" class="filters">
      <button class="btn btn--secondary" :aria-expanded="isFiltersOpen" aria-haspopup="true" ref="filterToggle" @click="toggleFilters">
        <span v-if="labels.showFilters" v-html="labels?.showFilters" />
        <Icon icon="chevron-down" />
      </button>
      <aside ref="filterWrapper" role="region">
        <h2 v-if="labels?.filterOn" v-html="labels?.filterOn" id="block-filters-title" />
        <form @submit.prevent="onSubmit" class="filters__form" role="form" aria-labelledby="block-filters-title">
          <div class="filters__block" v-if="loadingFilters">
            <Spinner />
          </div>

          <Fieldset
              v-else-if="filters.length > 0"
              v-for="filter in filters"
              :filters="filter"
              :key="filter.type"
              @update:selectedFilters="updateSelectedFilters" />

          <div class="filters__block" v-if="showDocuments">
            <div class="field-group">
              <label class="filters__label">
                <input
                    type="checkbox"
                    name="documents_only"
                    value="1"
                    :checked="isDocumentsOnly()"
                    @change="toggleDocumentsFilter($event)" />
                <span v-html="labels?.documentsOnly"></span>
              </label>
            </div>
          </div>

          <div v-if="!hideDateFilters" class="filters__block">
            <div class="field-group">
              <label for="date-from" v-html="labels?.dateFrom" />
              <input type="date"
                     name="date-from"
                     id="date-from"
                     v-model="dateFrom"
                     @change="validateDateRange('from')"
                     :max="dateTo || todayDate"
                     :min="minDate"
                     aria-describedby="date-from-error"
                     :class="{ 'field-error': dateErrors.from }"
              />
              <p id="date-from-error" v-if="dateErrors.from" class="field-message field-message--error" v-html="dateErrors.from" />
            </div>
            <div class="field-group">
              <label for="date-to" v-html="labels?.dateTo" />
              <input type="date"
                     name="date-to"
                     id="date-to"
                     v-model="dateTo"
                     @change="validateDateRange('to')"
                     :max="todayDate"
                     :min="dateFrom"
                     :class="{ 'field-error': dateErrors.to }"
              />
              <p id="date-to-error" v-if="dateErrors.to" class="field-message field-message--error" v-html="dateErrors.to" />
            </div>
          </div>
          <div class="filters__block" v-if="labels?.applyFilters">
            <button type="submit" class="btn btn--secondary" v-html="labels?.applyFilters"
                    :disabled="!hideDateFilters && (!!dateErrors.to||!!dateErrors.from)" />
          </div>
        </form>
      </aside>
    </div>

    <div :class="resultClasses">
      <SearchTerm v-if="showSearchTerm" />
      <Counter v-if="showCount" />
      <SkeletonCards v-for="i in skeletonLoaderCounter" :key="i" v-if="loading" />
      <Card
          v-else-if="results.length > 0"
          v-for="post in results"
          :key="post.id"
          :post="post" />
      <Alert v-else-if="error" :message="errorMessage" type="error" />
      <Alert v-else-if="0 ===results.length" :message="emptyMessage" />
      <Pagination v-if="!loading" @update:currentPage="updateCurrentPage" />
    </div>
  </section>
</template>

<script>
import { defineComponent, ref, onBeforeMount, reactive, computed, provide, onMounted, onUnmounted, watch } from 'vue';
import Alert from './components/Alert.vue';
import Card from './components/Card.vue';
import Counter from './components/Counter.vue';
import Icon from './components/Icon.vue';
import Fieldset from './components/Fieldset.vue';
import SearchForm from "./components/SearchForm.vue";
import SearchTerm from "./components/SearchTerm.vue";
import SkeletonCards from "./components/SkeletonCards.vue";
import Spinner from "./components/Spinner.vue";
import Pagination from "./components/Pagination.vue";

export default defineComponent( {
  name: 'Archive',
  components: {
    Alert,
    Card,
    Counter,
    Fieldset,
    Icon,
    Pagination,
    SearchForm,
    SearchTerm,
    Spinner,
    SkeletonCards,
  },
  props: {
    labels: {
      type: Object,
      default: () => ( {
            applyFilters: 'Apply filters',
            showFilters: 'Show filters',
            showMoreFilters: 'Show more',
            showLessFilters: 'Show less',
            filterOn: 'Filter on',
            dateFrom: 'Date from',
            dateTo: 'Date to',
            chip: {
              active: 'Active',
              inactive: 'Inactive',
            },
            pagination: {
              ariaLabel: 'Pagination',
              page: 'Page',
              prev: 'Previous page',
              next: 'Next page',
              live: 'Navigate to page %s'
            },
            counter: {
              plural: 'Found %s results.',
              singular: 'Found %s result.',
            },
            searchTermPrefix: 'You searched for `%s`',
            form: {
              button: 'Search',
              title: 'What are you looking for',
              placeholder: 'Search for one or more keywords',
            },
            errors: {
              date: {
                min: 'The `%s` date must be after the minimum date (%s).',
                before: 'The from date must be before the until date.',
                after: 'The until date must be behind the from date.',
              },
              empty: {
                searchedAndFiltered: 'No results found for \'%s\' with the selected filters.',
                searched: 'No results found for \'%s\'.',
                filtered: 'No results found for the selected filters.',
                default: 'No results found.',
              },
            },
            loading: {
              results: 'Loading results...',
              filters: 'Loading filters...',
              filtersWithFilters: 'Loading filters with selected filters...',
            }
          }
      ),
    },
    perPage: {
      type: Number,
      default: 6,
    },
    visibleFilters: {
      type: Number,
      default: 5
    },
    hideDateFilters: {
      type: Boolean,
      default: false
    },
    showSearchForm: {
      type: Boolean,
      default: false,
    },
    showSearchTerm: {
      type: Boolean,
      default: false
    },
    showCount: {
      type: Boolean,
      default: false
    },
    endpoint: {
      results: {
        type: String,
        default: 'vrgz/v1/incidents'
      },
      filters: {
        type: String,
        default: 'vrgz/v1/incident-types'
      }
    },
    orderby: {
      type: String,
      default: 'sort_meta'
    },
    order: {
      type: String,
      default: 'desc'
    },
    showDocuments: {
      type: Boolean,
      default: false
    }
  },
  setup( props ) {
    const initialized = ref( false );
    const clientWidth = ref( window.innerWidth );
    const isFiltersOpen = ref( clientWidth.value > 880 );
    const isCompact = ref( window.innerWidth < 880 );
    const loading = ref( false );
    const loadingFilters = ref( false );
    const error = ref( false );
    const errorFilters = ref( false );
    const errorMessage = ref( 'Something went wrong' );
    const filters = reactive( [] );
    const results = reactive( [] );
    const minDate = ref( null );
    const dateFrom = ref( null );
    const dateTo = ref( null );
    const dateErrors = ref( {
      from: '',
      to: ''
    } );
    const selectedFilters = reactive( {} );
    const totalResults = ref( 0 );
    const totalPages = ref( 1 );
    const currentPage = ref( 1 );
    const searchTerm = ref( '' );
    const liveFilterText = ref( '' );

    const allowFetchFilters = ref( props.endpoint.filters !== '' );

    const language = ref( document.documentElement.lang || navigator.language );

    const countLabel = computed( () => {
      const { singular, plural } = props.labels.counter;

      return ( totalResults.value === 1 ? singular : plural ).replace( '%s', new Intl.NumberFormat( language.value ).format( totalResults.value ) );
    } );

    const skeletonLoaderCounter = computed( () => {
      // Initialize a variable to store the total count.
      let totalCount = 0;

      // Loop through each filter in `filters`.
      filters.forEach( ( filter ) => {
        const selected = selectedFilters[ filter.taxonomy ];

        // If there are selected filters for this taxonomy, sum the counts.
        if ( !! selected && selected.length > 0 ) {
          filter?.choices.forEach( ( choice ) => {
            if ( selected.includes( choice.value ) ) {
              totalCount += choice.count;
            }
          } );
        }
      } );

      if ( 0 < totalCount && totalCount < props.perPage ) {
        return totalCount;
      }

      return props.perPage;
    } );

    const resultClasses = computed( () => (
        {
          'block--archive__results': true,
          'block--archive__results--error': !! error.value,
          'block--archive__results--loading': loading.value,
          'block--archive__results--empty': results.length === 0 && ! loading.value && ! error.value,
          'block--archive__results--no-filters': ! allowFetchFilters.value,
        }
    ) );

    const emptyMessage = computed( () => {
      // Remove `s` parameter from selectedFilters. Otherwise, it will always say "No results found for the selected filters.". This is because the search term is always added to the selectedFilters.
      if ( selectedFilters.hasOwnProperty( 's' ) ) {
        delete selectedFilters[ 's' ];
      }

      const { empty } = props.labels.errors;

      if ( searchTerm.value && Object.keys( selectedFilters ).length > 0 ) {
        return empty.searchedAndFiltered.replace( '%s', searchTerm.value );
      } else if ( searchTerm.value ) {
        return empty.searched.replace( '%s', searchTerm.value );
      } else if ( Object.keys( selectedFilters ).length > 0 ) {
        return empty.filtered;
      }

      return empty.default;
    } );

    const fetchResults = async () => {
      if ( !! loading.value ) {
        return;
      }

      try {
        loading.value = true;
        liveFilterText.value = props.labels.loading.results;
        const url = new URL( `${ window.vrgz_variables.rest.url }${ props.endpoint.results }` );

        url.searchParams.set( 'per_page', String( props.perPage ) );
        url.searchParams.set( 'orderby', props.orderby || 'modified' );
        url.searchParams.set( 'order', props.order || 'desc' );

        if ( searchTerm.value ) {
          url.searchParams.set( 'search', searchTerm.value ); // Parameter is the default WP search parameter.
          url.searchParams.set( 'orderby', 'relevance' ); // When searching, order by relevance. This overrides any other orderby parameter.
        }

        if ( currentPage.value > 1 ) {
          url.searchParams.set( 'page', String( currentPage.value ) );
        }
        if ( !! dateFrom.value ) {
          url.searchParams.set( 'after', dateFrom.value );
        }
        if ( !! dateTo.value ) {
          url.searchParams.set( 'before', dateTo.value );
        }

        if ( !! isCompact.value ) {
          url.searchParams.set( 'compact', String( 1 ) );
        }

        // Push any selected filter (of any taxonomy-type) to the URL.
        if ( Object.keys( selectedFilters ).length > 0 ) {
          Object.entries( selectedFilters ).forEach( ( [ key, value ] ) => url.searchParams.append( key, value.join( ',' ) ) );
        }

        url.searchParams.delete( 's' );

        await fetch( url.toString() )
            .then( response => {
              if ( ! response.ok ) {
                throw new Error( 'Network response was not ok, please try again later.' );
              }

              totalPages.value = parseInt( response.headers.get( 'X-Wp-Totalpages' ) ) || 1;
              totalResults.value = parseInt( response.headers.get( 'X-WP-Total' ) ) || 0;

              // If the current page is higher than the total pages, reset to 1.
              currentPage.value = currentPage.value > totalPages.value ? 1 : currentPage.value;

              return response.json();
            } )
            .then( data => results.splice( 0, results.length, ...data ) )
            .finally( () => {
              loading.value = false;
              error.value = false;
              liveFilterText.value = countLabel.value;

              // No need to keep the per_page parameter in the URL.
              url.searchParams.delete( 'per_page' );
              if ( !! isCompact.value ) {
                url.searchParams.delete( 'compact' );
              }

              // Remove parameters not in selectedFilters and remove empty parameters
              const validParams = new URLSearchParams();
              Object.keys( selectedFilters ).forEach( key => {
                if ( selectedFilters[ key ].length > 0 ) {
                  validParams.set( key, selectedFilters[ key ].join( ',' ) );
                }
              } );

              // Also include current filters not in selectedFilters.
              url.searchParams.forEach( ( value, key ) => {
                if ( selectedFilters[ key ] && selectedFilters[ key ].length > 0 ) {
                  validParams.set( key, selectedFilters[ key ].join( ',' ) );
                }
              } );

              // Reset the liveText after a brief delay to allow for subsequent announcements.
              setTimeout( () => liveFilterText.value = '', 1000 );
            } )
            .catch( error => {
              error.value = true;
              errorMessage.value = error;
              loading.value = false;
            } );
      } catch ( error ) {
        errorMessage.value = error;
        error.value = true;
        loading.value = false;
      }
    };

    const fetchFilters = async () => {
      if ( ! allowFetchFilters.value ) {
        // Vacancy archive has no filters.
        return;
      }

      if ( !! loadingFilters.value ) {
        // Have a little patience, my friend.
        return;
      }

      if ( ! initialized.value ) {
        loadingFilters.value = true;
      }

      try {
        const url = new URL( `${ window.vrgz_variables.rest.url }${ props.endpoint.filters }` );

        if ( !! filters && filters.length > 0 ) {
          // Get those that are actually taxonomies (exclude searchTerm & dates).
          const allowedTaxonomies = filters.map( filter => filter.type );
          if ( allowedTaxonomies.length > 0 && Object.keys( selectedFilters ).length > 0 ) {
            Object.entries( selectedFilters ).forEach( ( [ key, value ] ) => {
              if ( allowedTaxonomies.includes( key ) ) {
                url.searchParams.append( key, value.join( ',' ) );
              }
            } );
            liveFilterText.value = props.labels.loading.filtersWithFilters;
          }
        } else {
          liveFilterText.value = props.labels.loading.filters;
        }

        if ( searchTerm.value ) {
          // Used for the count.
          url.searchParams.set( 'search', searchTerm.value ); // Parameter is the default WP search parameter.
        }

        if ( selectedFilters[ 'documents_only' ] ) {
          url.searchParams.set( 'documents_only', selectedFilters[ 'documents_only' ] );
        }

        await fetch( url.toString() )
            .then( response => {
              if ( ! response.ok ) {
                throw new Error( 'Network response was not ok, please try again later.' );
              }

              if ( response.headers.has( 'X-Wp-MinDate' ) ) {
                minDate.value = response.headers.get( 'X-Wp-MinDate' );
              }

              return response.json();
            } )
            .then( data => filters.splice( 0, filters.length, ...data ) )
            .finally( () => {
              loadingFilters.value = false;
              // Reset the liveText after a brief delay to allow for subsequent announcements.
              setTimeout( () => liveFilterText.value = '', 1000 );
            } )
            .catch( error => resetFetchingFilters( error ) );
      }
      catch ( error ) {
        resetFetchingFilters( error );
      }
    };


    const resetFetchingFilters = ( error ) => {
      console.error( error );
      loadingFilters.value = false;
      errorFilters.value = true;
    };

    /**
     * Get today's date in YYYY-MM-DD format.
     *
     * @type {ComputedRef<string>}
     */
    const todayDate = computed( () => {
      const today = new Date();
      return today.toISOString().split( 'T' )[ 0 ]; // Get the date in "YYYY-MM-DD" format
    } );

    const updateUrlWithFilters = () => {
      const validParams = new URLSearchParams();

      // Keep selected filters
      Object.keys( selectedFilters ).forEach( key => {
        if ( selectedFilters[ key ].length > 0 ) {
          validParams.set( key, selectedFilters[ key ].join( ',' ) );
        }
      } );

      if ( searchTerm.value ) {
        validParams.set( 's', searchTerm.value );
      }

      // Keep date filters
      if ( dateFrom.value ) {
        validParams.set( 'after', dateFrom.value );
      }
      if ( dateTo.value ) {
        validParams.set( 'before', dateTo.value );
      }

      // Keep the current page.
      if ( currentPage.value > 1 ) {
        validParams.set( 'page', String( currentPage.value ) );
      } else if ( validParams.has( 'page' ) ) {
        validParams.delete( 'page' );
      }

      // Push the new URL state without removing parameters
      window.history.pushState( null, '', validParams.toString() ? `${ window.location.pathname }?${ validParams.toString() }` : window.location.pathname );
    };

    const onSubmit = () => {
      fetchResults();
      fetchFilters();
    }

    const toggleDocumentsFilter = ( event ) => {
      const { checked } = event.target;
      if ( checked ) {
        selectedFilters[ 'documents_only' ] = [ 1 ];
      } else {
        delete selectedFilters[ 'documents_only' ];
      }
    };

    /**
     * Set the selected filters.
     *
     */
    const updateSelectedFilters = ( { type, value, checked } ) => {
      if ( ! selectedFilters[ type ] ) {
        selectedFilters[ type ] = [];
      }

      if ( checked ) {
        if ( ! selectedFilters[ type ].includes( value ) ) {
          selectedFilters[ type ].push( value );
        }
      } else {
        const index = selectedFilters[ type ].indexOf( value );
        if ( index !== -1 ) {
          selectedFilters[ type ].splice( index, 1 );
        }
        if ( selectedFilters[ type ].length === 0 ) {
          delete selectedFilters[ type ];
        }
      }
    };

    const updateCurrentPage = ( page ) => {
      currentPage.value = parseInt( page ) > totalPages.value ? 1 : page;
      liveFilterText.value = props.labels.pagination.live.replace( '%s', currentPage.value );
    };

    const initializeFromUrlParams = () => {
      const params = new URLSearchParams( window.location.search );

      // Set the search term from URL params if available.
      if ( params.has( 's' ) ) {
        searchTerm.value = params.get( 's' ); // Set searchTerm from URL
      } else {
        searchTerm.value = ''; // Default to empty if not found
      }

      // Set date filters from URL params if available.
      if ( params.has( 'after' ) ) {
        dateFrom.value = params.get( 'after' );
        params.delete( 'after' );
      }
      if ( params.has( 'before' ) ) {
        dateTo.value = params.get( 'before' );
        params.delete( 'before' );
      }

      if ( params.has( 'page' ) ) {
        currentPage.value = parseInt( params.get( 'page' ) );
        params.delete( 'page' );
      }

      // Set selected filters from URL params if available.
      params?.forEach( ( value, key ) => {
        // Split the parameter by comma to create an array of strings.
        const valuesArray = value.split( ',' );

        // Convert strings to numbers if they are numeric, otherwise keep them as strings.
        selectedFilters[ key ] = valuesArray.map( v => {
          const num = parseInt( v, 10 );
          return isNaN( num ) ? v : num;
        } );
      } );

      if ( params.has( 'documents_only' ) ) {
        selectedFilters[ 'documents_only' ] = [ 1 ];
      }
    };

    const updateClientWidth = () => {
      clientWidth.value = window.innerWidth;
      isCompact.value = window.innerWidth < 880;
    };

    const toggleFilters = () => isFiltersOpen.value = ! isFiltersOpen.value;


    // Function to validate the date range.
    const validateDateRange = ( type ) => {
      // Reset errors.
      dateErrors.value.from = '';
      dateErrors.value.to = '';

      if ( ! dateFrom.value || ! dateTo.value ) {
        return;
      }

      dateErrors.value[ type ] = validateDate(
          new Date( dateFrom.value ),
          new Date( dateTo.value ),
          new Date( minDate.value ),
          type || 'from'
      );
    };

    /**
     * Validate the date range between the from and to date.
     * Returns an error.
     *
     * @param {Date} fromDate The from date of the from-field.
     * @param {Date} toDate The to date of the to-field.
     * @param {Date} minDate The minimum allowed date.
     * @param {string} type The type, from or to.
     * @return {string}
     */
    const validateDate = ( fromDate, toDate, minDate, type = '' ) => {

      const { before, after, min } = props.labels.errors.date;
      const { dateFrom, dateTo } = props.labels;

      switch ( type ) {
        case 'from':
          if ( fromDate < minDate ) {
            let replacements = [ dateFrom.toLowerCase(), new Intl.DateTimeFormat( language.value, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            } ).format( minDate ) ];
            return min.replace( /%s/g, () => replacements.shift() );
          } else if ( fromDate > toDate ) {
            return before;
          }
          break;
        case 'to':
          if ( toDate < minDate ) {
            let replacements = [ dateTo.toLowerCase(), new Intl.DateTimeFormat( language.value, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            } ).format( minDate ) ];
            return min.replace( /%s/g, () => replacements.shift() );
          } else if ( toDate < fromDate ) {
            return after;
          }
          break;
      }

      return '';
    }

    const isDocumentsOnly = () => selectedFilters[ 'documents_only' ] && selectedFilters[ 'documents_only' ].length > 0;

    const handlePopState = () => {
      initializeFromUrlParams();
      fetchFilters();
      fetchResults();
    };

    provide( 'language', language );

    // Chips.
    provide( 'chipLabels', props.labels.chip );

    // Filters.
    provide( 'amountVisibleFilters', props.visibleFilters );
    provide( 'buttonLabels', {
      more: props.labels.showMoreFilters,
      less: props.labels.showLessFilters
    } );
    provide( 'selectedFilters', selectedFilters );

    provide( 'count', {
      count: totalResults,
      label: countLabel
    } );

    provide( 'searchForm', {
      label: props.labels.searchTermPrefix,
      term: searchTerm,
      ...props.labels.form
    } );

    // Pagination.
    provide( 'totalPages', totalPages );
    provide( 'currentPage', currentPage );
    provide( 'paginationLabels', props.labels.pagination );

    // Card
    provide( 'card_heading_level', 2 );

    onBeforeMount( () => {
      handlePopState();
      initialized.value = true;
    } );

    onMounted( () => {
      window.addEventListener( 'popstate', handlePopState );
      window.addEventListener( 'resize', updateClientWidth );
    } );

    onUnmounted( () => {
      window.removeEventListener( 'popstate', handlePopState );
      window.removeEventListener( 'resize', updateClientWidth );
    } );

    watch( [ dateFrom, dateTo, currentPage, selectedFilters, searchTerm ], () => updateUrlWithFilters());
    watch( [ searchTerm, selectedFilters ], () => fetchFilters() );
    watch( [ currentPage ], () => {
          // Adding initialized check to prevent fetchResults from running on initial load.
          if ( initialized.value ) {
            fetchResults();
          }
        }
    );

    return {
      allowFetchFilters,
      error,
      errorMessage,
      emptyMessage,
      loading,
      loadingFilters,
      filters,
      results,
      dateTo,
      dateFrom,
      dateErrors,
      onSubmit,
      todayDate,
      minDate,
      isDocumentsOnly,
      toggleDocumentsFilter,
      updateSelectedFilters,
      updateCurrentPage,
      isFiltersOpen,
      toggleFilters,
      resultClasses,
      validateDateRange,
      skeletonLoaderCounter,
      liveFilterText,
      showDocuments: props.showDocuments,
    };
  },
} );
</script>
